import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../config/apiConfig";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import "./CurrentAffairsQuizAdmin.css";

const DEFAULT_SUBJECT = "Reasoning";

const formatAttemptedAt = (value) => {
  if (!value) return "No attempts yet";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No attempts yet";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function SubjectFilter({ value, onChange, loading, subjects, loadingSubjects }) {
  return (
    <div className="ca-admin-filter-shell ca-admin-filter-shell-compact">
      <div className="ca-admin-filter ca-admin-filter-wide ca-admin-filter-compact">
        <label htmlFor="quiz-attempt-subject">Subject</label>
        <select
          id="quiz-attempt-subject"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={loadingSubjects}
        >
          {loadingSubjects ? <option value="">Loading subjects...</option> : null}
          {!loadingSubjects &&
            subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
        </select>
      </div>
      <div className={`ca-admin-live-state ${loading ? "loading" : ""}`}>
        {loading ? "Refreshing stats..." : "Auto-updates on subject change"}
      </div>
    </div>
  );
}

function StatsSummaryCards({ totalQuizzes, totalAttemptedCandidates, loading }) {
  if (loading) {
    return (
      <div className="ca-admin-stats-grid ca-admin-stats-grid-compact">
        <div className="ca-admin-stat-card skeleton" />
        <div className="ca-admin-stat-card skeleton" />
      </div>
    );
  }

  return (
    <div className="ca-admin-stats-grid ca-admin-stats-grid-compact">
      <div className="ca-admin-stat-card">
        <span>Total Quizzes</span>
        <strong>{totalQuizzes}</strong>
      </div>
      <div className="ca-admin-stat-card">
        <span>Total Attempted Candidates</span>
        <strong>{totalAttemptedCandidates}</strong>
      </div>
    </div>
  );
}

function QuizAttemptStatsTable({ quizzes, loading }) {
  if (loading) {
    return (
      <div className="ca-admin-table-wrap">
        <div className="ca-admin-table-skeleton-row" />
        <div className="ca-admin-table-skeleton-row" />
        <div className="ca-admin-table-skeleton-row" />
        <div className="ca-admin-table-skeleton-row" />
      </div>
    );
  }

  return (
    <div className="ca-admin-table-wrap">
      <table className="ca-admin-table">
        <thead>
          <tr>
            <th>Quiz Title</th>
            <th>Topic</th>
            <th>Sub Topic</th>
            <th>Total Questions</th>
            <th>Total Marks</th>
            <th>Attempted Candidates</th>
            <th>Last Attempted At</th>
          </tr>
        </thead>
        <tbody>
          {quizzes.map((quiz) => (
            <tr key={quiz.quizId}>
              <td data-label="Quiz Title">{quiz.title || `Quiz #${quiz.quizId}`}</td>
              <td data-label="Topic">{quiz.topic || "-"}</td>
              <td data-label="Sub Topic">{quiz.subTopic || "-"}</td>
              <td data-label="Total Questions">{quiz.totalQuestions ?? "-"}</td>
              <td data-label="Total Marks">{quiz.totalMarks ?? "-"}</td>
              <td data-label="Attempted Candidates">
                <span className="ca-admin-attempt-badge">{quiz.attemptedCandidates || 0}</span>
              </td>
              <td data-label="Last Attempted At">{formatAttemptedAt(quiz.lastAttemptedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminQuizAttemptStatsPage() {
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(DEFAULT_SUBJECT);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState("");
  const [message, setMessage] = useState("");

  const fetchStats = useCallback(async (subject) => {
    const safeSubject = String(subject || "").trim();
    if (!safeSubject) return;

    setLoading(true);
    setErrorType("");
    setMessage("");

    try {
      const response = await apiFetch(
        `/quizzes/admin/attempt-stats?subject=${encodeURIComponent(safeSubject)}`,
        { auth: true }
      );
      const data = await readJsonSafe(response);

      if (response.status === 403) {
        setStats(null);
        setErrorType("forbidden");
        return;
      }

      if (!response.ok) {
        throw new Error(data?.message || "Unable to load quiz attempt stats. Please try again.");
      }

      setStats({
        subject: data?.subject || safeSubject,
        totalQuizzes: Number(data?.totalQuizzes || 0),
        totalAttemptedCandidates: Number(data?.totalAttemptedCandidates || 0),
        quizzes: Array.isArray(data?.quizzes) ? data.quizzes : [],
      });
    } catch (error) {
      setStats(null);
      setErrorType("error");
      setMessage(error.message || "Unable to load quiz attempt stats. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSubjects = async () => {
      setLoadingSubjects(true);
      try {
        const response = await fetch(`${API_BASE_URL}/questions/subjects`);
        if (!response.ok) {
          throw new Error("Failed to load subjects.");
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data.filter(Boolean) : [];
        const normalizedSubjects = list.length ? list : [DEFAULT_SUBJECT];
        const initialSubject =
          normalizedSubjects.find(
            (subject) => String(subject).toLowerCase() === DEFAULT_SUBJECT.toLowerCase()
          ) || normalizedSubjects[0];

        if (!mounted) return;

        setSubjects(normalizedSubjects);
        setSelectedSubject(initialSubject);
        fetchStats(initialSubject);
      } catch {
        if (!mounted) return;
        setSubjects([DEFAULT_SUBJECT]);
        setSelectedSubject(DEFAULT_SUBJECT);
        fetchStats(DEFAULT_SUBJECT);
      } finally {
        if (mounted) {
          setLoadingSubjects(false);
        }
      }
    };

    loadSubjects();

    return () => {
      mounted = false;
    };
  }, [fetchStats]);

  const handleSubjectChange = (subject) => {
    const safeSubject = String(subject || "").trim();
    if (!safeSubject) return;
    setSelectedSubject(safeSubject);
    fetchStats(safeSubject);
  };

  const quizzes = useMemo(() => (Array.isArray(stats?.quizzes) ? stats.quizzes : []), [stats]);
  const isEmpty = !loading && !errorType && quizzes.length === 0;

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header ca-admin-header-modern">
        <div>
          <p className="ca-admin-hero-kicker">Analytics Console</p>
          <h1>Quiz Attempt Stats</h1>
          <p className="ca-admin-subtitle">
            Review quiz attempt analytics for each subject and track candidate activity.
          </p>
        </div>
        <div className="ca-admin-actions">
          <Link to="/admin/dashboard" className="ca-admin-btn">
            Back to Admin
          </Link>
        </div>
      </div>

      <section className="ca-admin-form-card ca-admin-analytics-card">
        <div className="ca-admin-analytics-topbar">
          <SubjectFilter
            value={selectedSubject}
            onChange={handleSubjectChange}
            loading={loading}
            subjects={subjects}
            loadingSubjects={loadingSubjects}
          />
          <div className="ca-admin-current-filter compact">
            <span>Viewing</span>
            <strong>{stats?.subject || selectedSubject}</strong>
          </div>
          <StatsSummaryCards
            totalQuizzes={stats?.totalQuizzes || 0}
            totalAttemptedCandidates={stats?.totalAttemptedCandidates || 0}
            loading={loading}
          />
        </div>
      </section>

      {errorType === "forbidden" ? (
        <p className="ca-admin-msg err">You do not have permission to access this page.</p>
      ) : null}
      {errorType === "error" ? (
        <div className="ca-admin-msg err ca-admin-msg-block">
          <span>{message || "Unable to load quiz attempt stats. Please try again."}</span>
          <button
            type="button"
            className="ca-admin-btn"
            onClick={() => fetchStats(selectedSubject)}
          >
            Retry
          </button>
        </div>
      ) : null}

      {!errorType ? (
        <section className="ca-admin-form-card ca-admin-analytics-card">
          {isEmpty ? (
            <p className="ca-admin-empty-state">No quizzes found for this subject.</p>
          ) : (
            <QuizAttemptStatsTable quizzes={quizzes} loading={loading} />
          )}
        </section>
      ) : null}
    </div>
  );
}

export default AdminQuizAttemptStatsPage;
